import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { collection, getDocs, addDoc, query, where } from "firebase/firestore";
import { db } from "../firebase-config";
import Collections from "../collections";

const AddProductModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    title: "",
    brand: "",
    price: "",
    vendor: "",
    quantity: "",
    categoryId: "",
    units: "",
    isActive: true,
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "95%",
    maxWidth: 640,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    overflowY: "auto",
  };

  useEffect(() => {
    if (!open) return;
    const fetchCategories = async () => {
      const snap = await getDocs(collection(db, Collections.INVENTORY_CATEGORY));
      setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchCategories();
  }, [open]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setError("");

    const newErrors = {};
    Object.entries(formData).forEach(([k, v]) => {
      if (k !== "isActive" && !v) newErrors[k] = "Required";
    });

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const productQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("title", "==", formData.title),
        where("brand", "==", formData.brand),
        where("vendor", "==", formData.vendor)
      );

      const existing = await getDocs(productQuery);
      if (!existing.empty) {
        setError("Product already exists. Update quantity instead.");
        setLoading(false);
        return;
      }

      await addDoc(collection(db, Collections.INVENTORY_ITEMS), {
        id: uuidv4(),
        ...formData,
        isActive: formData.isActive,
        availableQuantity: Number(formData.quantity),
        soldQuantity: 0,
        createdAt: new Date().toISOString(),
      });

      onClose();
      setFormData({
        title: "",
        brand: "",
        price: "",
        vendor: "",
        quantity: "",
        categoryId: "",
        units: "",
        isActive: true,
      });
    } catch {
      setError("Failed to add product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h2" mb={1}>Add Inventory Item</Typography>
        <Typography color="text.secondary" mb={3}>
          Create a new product under a category
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Typography variant="h3" mb={1}>Basic Info</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Item Name"
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                error={!!errors.brand}
                helperText={errors.brand}
                fullWidth
              />
            </Grid>
          </Grid>

          <Typography variant="h3" mt={3} mb={1}>Pricing</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price (£)"
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                error={!!errors.price}
                helperText={errors.price}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor"
                name="vendor"
                value={formData.vendor}
                onChange={handleChange}
                error={!!errors.vendor}
                helperText={errors.vendor}
                fullWidth
              />
            </Grid>
          </Grid>

          <Typography variant="h3" mt={3} mb={1}>Stock</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Quantity"
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                error={!!errors.quantity}
                helperText={errors.quantity}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Units"
                name="units"
                value={formData.units}
                onChange={handleChange}
                error={!!errors.units}
                helperText={errors.units}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Category"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                error={!!errors.categoryId}
                helperText={errors.categoryId}
                fullWidth
              >
                {categories.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.category}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                }
                color="success"
              />
            }
            label="Show in Mobile"
            sx={{ mt: 2, ml: 0 }}
          />

          <Box mt={4} display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={onClose} variant="outlined">Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Add Item"}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default AddProductModal;
